import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { PlatformModal } from './PlatformModal.js';

/**
 * Set/Update/Unset Founding Customer (Phase C-3). Mirrors the C-2 server Zod:
 *   - founding=true  → lockedRate (decimal > 0) AND lockEndsAt (future) required
 *   - founding=false → lockedRate / lockEndsAt must be omitted (cleared)
 * lockEndsAt is captured via a native date input and sent to the API as a full
 * ISO datetime (the server uses z.string().datetime()).
 */
const schema = z
  .object({
    isFoundingCustomer: z.boolean(),
    lockedRate: z.string().trim().optional(),
    lockEndsAt: z.string().trim().optional(), // 'YYYY-MM-DD' from <input type="date">
    reason: z.string().trim().min(1, 'Reason is required').max(500, 'Reason must be 500 characters or fewer'),
  })
  .superRefine((v, ctx) => {
    if (v.isFoundingCustomer) {
      if (!v.lockedRate) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['lockedRate'], message: 'Locked rate is required.' });
      } else if (!/^\d+(\.\d+)?$/.test(v.lockedRate) || Number(v.lockedRate) <= 0) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['lockedRate'], message: 'Enter a positive amount.' });
      }
      if (!v.lockEndsAt) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['lockEndsAt'], message: 'Lock end date is required.' });
      } else if (new Date(v.lockEndsAt).getTime() <= Date.now()) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['lockEndsAt'], message: 'Lock end date must be in the future.' });
      }
    }
  });
type Values = z.infer<typeof schema>;

export function SetFoundingModal({
  open,
  onClose,
  currentIsFounding,
  isSubmitting = false,
  errorMessage,
  onConfirm,
}: {
  open: boolean;
  onClose: () => void;
  currentIsFounding: boolean;
  isSubmitting?: boolean;
  errorMessage?: string;
  onConfirm: (body: {
    isFoundingCustomer: boolean;
    lockedRate?: string;
    lockEndsAt?: string;
    reason: string;
  }) => Promise<void> | void;
}): JSX.Element | null {
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
    reset,
  } = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { isFoundingCustomer: !currentIsFounding, reason: '' },
  });

  if (!open) return null;

  const founding = watch('isFoundingCustomer');

  const handleClose = (): void => {
    reset();
    onClose();
  };

  const submit = (v: Values): void => {
    if (v.isFoundingCustomer) {
      void onConfirm({
        isFoundingCustomer: true,
        lockedRate: v.lockedRate,
        // Convert the date-only value to a full ISO datetime for the server.
        lockEndsAt: new Date(v.lockEndsAt as string).toISOString(),
        reason: v.reason,
      });
    } else {
      void onConfirm({ isFoundingCustomer: false, reason: v.reason });
    }
  };

  return (
    <PlatformModal
      open={open}
      onClose={handleClose}
      title="Founding customer"
      footer={
        <>
          <button
            type="button"
            onClick={handleClose}
            disabled={isSubmitting}
            className="px-4 py-2 border rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="set-founding-form"
            disabled={isSubmitting}
            className="px-4 py-2 rounded-md font-medium bg-indigo-primary text-white hover:opacity-90 disabled:opacity-50"
          >
            {isSubmitting ? 'Submitting…' : 'Save'}
          </button>
        </>
      }
    >
      <form id="set-founding-form" onSubmit={handleSubmit(submit)}>
        <p className="text-sm text-gray-700 mb-3">
          Currently {currentIsFounding ? 'a Founding Customer' : 'not a Founding Customer'}.
        </p>
        <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-3">
          <input type="checkbox" {...register('isFoundingCustomer')} disabled={isSubmitting} />
          Founding Customer
        </label>

        {founding ? (
          <div className="space-y-3">
            <div>
              <label htmlFor="founding-rate" className="block text-sm font-medium text-gray-700 mb-1">
                Locked rate (USD / employee / month)
              </label>
              <input
                id="founding-rate"
                type="text"
                inputMode="decimal"
                {...register('lockedRate')}
                disabled={isSubmitting}
                placeholder="e.g. 8.50"
                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-primary"
              />
              {errors.lockedRate ? <p className="mt-1 text-sm text-red-600">{errors.lockedRate.message}</p> : null}
            </div>
            <div>
              <label htmlFor="founding-lock-ends" className="block text-sm font-medium text-gray-700 mb-1">
                Lock ends
              </label>
              <input
                id="founding-lock-ends"
                type="date"
                {...register('lockEndsAt')}
                disabled={isSubmitting}
                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-primary"
              />
              {errors.lockEndsAt ? <p className="mt-1 text-sm text-red-600">{errors.lockEndsAt.message}</p> : null}
            </div>
          </div>
        ) : null}

        <label htmlFor="founding-reason" className="block text-sm font-medium text-gray-700 mb-1 mt-3">
          Reason
        </label>
        <textarea
          id="founding-reason"
          {...register('reason')}
          disabled={isSubmitting}
          rows={3}
          className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-primary"
          placeholder="Written record for the audit trail…"
        />
        {errors.reason ? <p className="mt-1 text-sm text-red-600">{errors.reason.message}</p> : null}
        {errorMessage ? (
          <p className="mt-2 text-sm text-red-600" role="alert">
            {errorMessage}
          </p>
        ) : null}
      </form>
    </PlatformModal>
  );
}
